// @ts-check

import React, {useContext, useRef} from 'react';
import {Divider, FormControl, IconButton, InputAdornment, MenuItem, Select, TextField, Tooltip} from '@mui/material';
import {useLocation, useHistory} from 'react-router';
import SelectTabContextProvider from '../../components/SelectTabContext/SelectTabContext';
import { Close, Search } from '@material-ui/icons';

/**
 * 
 * @typedef DashboardSearchOption
 * @property {string} key
 * @property {string} value
 */

export default function DashboardSearch() {
  const inputRef = useRef(/** @type {HTMLInputElement | null} */(null));
  const history = useHistory();
  const location = useLocation();
  const {setSearch, searchText, setSearchText, searchAny, setSearchAny} = useContext(SelectTabContextProvider);

  /**
   * @type {DashboardSearchOption[]}
   */
  const searchOptions = [
    {key: 'accountNumber', value: 'Account No'},
    {key: 'clientReferenceNumber', value: 'Client Account Number'},
    {key: 'lastFirstName', value: 'Last Name, First Name'},
    {key: 'firstName', value: 'First Name'},
    {key: 'lastName', value: 'Last Name'},
    {key: 'socialSecurityNumber', value: 'SSN'},
    {key: 'medicalRecordNumber', value: 'Patient MRN'},
    // {key: 'mobileNumber', value: 'Phone Number'},
    {key: 'clientNumber', value: 'Client Code'},
    // {key: 'addressLine1', value: 'Address'},
    {key: 'placementDate', value: 'Placement Date'},
    {key: 'employerName', value: 'Employer Name'},
    {key: 'address', value: 'Address'},
    {key: 'phone', value: 'Phone'},
  ];

  const submitForm = () => {
    setSearch(true);
    if (! location.pathname.startsWith('/patient-search')) {
      history.push({
        pathname: '/patient-search',
        state: {
          fromWorkQueue: location?.state?.fromWorkQueue ? location.state.fromWorkQueue : '',
          workQueueName: location?.state?.workQueueName ? location.state.workQueueName : '',
        },
      });
    }
  }

  /**
   * @param {React.FormEvent<HTMLFormElement>} e 
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    submitForm();
  };

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const handleInputChange = (e) => {
    setSearchText(e.target.value);
  };

  /**
   * @param {any} e
   */
  const handleSelectChange = (e) => {
    const item = searchOptions.find(option => (option.key === e.target.value));
    setSearchAny(item);
  };

  /**
   * @param {React.MouseEvent<HTMLButtonElement>} e
   */
  const handleClearClick = (e) => {
    setSearchText('');
    inputRef.current?.focus();
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormControl size="small" sx={{ display: 'flex', flexFlow: 'row', alignItems: 'center', gap: 2 }}>
        <Select
          variant="standard"
          size="small"
          onChange={handleSelectChange}
          value={searchAny.key}
          displayEmpty={true}
          disableUnderline={true}
          MenuProps={{
            transformOrigin: { horizontal: 'left', vertical: 'top' },
            anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
          }}
        >
          {searchOptions.map(option => (
            <MenuItem key={option.key} value={option.key}>{option.value}</MenuItem>
          ))}
        </Select>
        <Divider orientation="vertical" flexItem={true} />
        <Tooltip title="Press enter to search">
          <TextField
            variant="standard"
            hiddenLabel={true}
            inputRef={inputRef}
            size="small"
            placeholder="Search..."
            value={searchText}
            onChange={handleInputChange}
            className="form-textfield form-textfield-label"
            fullWidth={false}
            disabled={searchAny?.key === 'udwLookup'}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: '40vw' }}
          />
        </Tooltip>
        {(searchText) && (
          <Tooltip title="Clear">
            <IconButton color="inherit" type="button" onClick={handleClearClick}>
              <Close />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Search">
          <IconButton tabIndex={-1} color="inherit" type="submit" className="screen-reader-only">
            <Search />
          </IconButton>
        </Tooltip>
      </FormControl>
    </form>
  );
}
